import React, {createContext, useContext, useEffect, useState} from 'react';
import {
  AccessibilityContrastEnum,
  AccessibilityDyslexiaEnum,
  AccessibilityFontSizeEnum
} from "app/shared/accessibility/accessibility.enum";
import {LOCAL_STORAGE_KEYS} from "app/shared/util/local-storage-keys";
import {Storage} from "react-jhipster";

interface AccessibilityContextType {
  contrast: AccessibilityContrastEnum;
  dyslexia: AccessibilityDyslexiaEnum;
  fontSize: AccessibilityFontSizeEnum;
  onSetContrast: (contrast: AccessibilityContrastEnum) => void;
  onSetDyslexia: (dyslexia: AccessibilityDyslexiaEnum) => void;
  onIncreaseFontSize: () => void;
  onDecreaseFontSize: () => void;
  onResetFontSize: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const [contrast, setContrast] = useState<AccessibilityContrastEnum>(AccessibilityContrastEnum.NON_IMPAIRED);
  const [dyslexia, setDyslexia] = useState<AccessibilityDyslexiaEnum>(AccessibilityDyslexiaEnum.NON_DYSLEXIC);
  const [fontSize, setFontSize] = useState<AccessibilityFontSizeEnum>(AccessibilityFontSizeEnum.FONT_SIZE_M);

  useEffect(() => {
    initLocalStorage();
  }, []);

  useEffect(() => {
    Storage.local.set(LOCAL_STORAGE_KEYS.ACCESSIBILITY_CONTRAST, contrast);
  }, [contrast]);

  useEffect(() => {
    Storage.local.set(LOCAL_STORAGE_KEYS.ACCESSIBILITY_DYSLEXIA, dyslexia);
  }, [dyslexia]);

  useEffect(() => {
    Storage.local.set(LOCAL_STORAGE_KEYS.ACCESSIBILITY_FONT_SIZE, fontSize);
  }, [fontSize]);

  useEffect(() => {
    applyCssChangesToBodyHtmlElement();
  }, [contrast, dyslexia, fontSize]);

  const initLocalStorage = () => {
    const contrastStateLocalStorage = Storage.local.get(LOCAL_STORAGE_KEYS.ACCESSIBILITY_CONTRAST) as AccessibilityContrastEnum;
    const dyslexiaStateLocalStorage = Storage.local.get(LOCAL_STORAGE_KEYS.ACCESSIBILITY_DYSLEXIA) as AccessibilityDyslexiaEnum;
    const fontSizeStateLocalStorage = Storage.local.get(LOCAL_STORAGE_KEYS.ACCESSIBILITY_FONT_SIZE) as AccessibilityFontSizeEnum;

    if (contrastStateLocalStorage) {
      setContrast(contrastStateLocalStorage);
    }

    if (dyslexiaStateLocalStorage) {
      setDyslexia(dyslexiaStateLocalStorage);
    }

    if (fontSizeStateLocalStorage) {
      setFontSize(fontSizeStateLocalStorage);
    }
  };

  const onSetContrast = (contrast: AccessibilityContrastEnum) => {
    setContrast(contrast);
    setDyslexia(AccessibilityDyslexiaEnum.NON_DYSLEXIC);
  };

  const onSetDyslexia = (dyslexia: AccessibilityDyslexiaEnum) => {
    setDyslexia(dyslexia);
    setContrast(AccessibilityContrastEnum.NON_IMPAIRED);
  };

  const onIncreaseFontSize = () => {
    setFontSize((prevState) => {
      if (prevState >= AccessibilityFontSizeEnum.FONT_SIZE_3XL) {
        return prevState;
      }

      return prevState + 1;
    });
  };

  const onDecreaseFontSize = () => {
    setFontSize((prevState) => {
      if (prevState <= AccessibilityFontSizeEnum.FONT_SIZE_3XS) {
        return prevState;
      }

      return prevState - 1;
    });
  };

  const onResetFontSize = () => {
    setFontSize(AccessibilityFontSizeEnum.FONT_SIZE_M);
  };

  const applyCssChangesToBodyHtmlElement = () => {
    if (!document) {
      return;
    }

    const elements = document.getElementsByTagName('body');

    const body = elements && elements.item(0);

    if (body) {
      body.className = `${contrastDyslexiaCssStyle()} ${fontSizeCssStyle()}`;
    }
  };

  const contrastDyslexiaCssStyle = () => {
    if (contrast === AccessibilityContrastEnum.IMPAIRED && dyslexia === AccessibilityDyslexiaEnum.NON_DYSLEXIC) {
      return 'accessibility-contrast';
    } else if (contrast === AccessibilityContrastEnum.NON_IMPAIRED && dyslexia === AccessibilityDyslexiaEnum.DYSLEXIC) {
      return 'accessibility-dyslexia';
    }

    return '';
  };

  const fontSizeCssStyle = () => {
    switch (fontSize) {
      case AccessibilityFontSizeEnum.FONT_SIZE_3XS:
        return 'accessibility-font-size-3xs';
      case AccessibilityFontSizeEnum.FONT_SIZE_2XS:
        return 'accessibility-font-size-2xs';
      case AccessibilityFontSizeEnum.FONT_SIZE_XS:
        return 'accessibility-font-size-xs';
      case AccessibilityFontSizeEnum.FONT_SIZE_S:
        return 'accessibility-font-size-s';
      case AccessibilityFontSizeEnum.FONT_SIZE_L:
        return 'accessibility-font-size-l';
      case AccessibilityFontSizeEnum.FONT_SIZE_XL:
        return 'accessibility-font-size-xl';
      case AccessibilityFontSizeEnum.FONT_SIZE_2XL:
        return 'accessibility-font-size-2xl';
      case AccessibilityFontSizeEnum.FONT_SIZE_3XL:
        return 'accessibility-font-size-3xl';
      default:
        return '';
    }
  };

  return (
    <AccessibilityContext.Provider
      value={{
        contrast,
        dyslexia,
        fontSize,
        onSetContrast,
        onSetDyslexia,
        onIncreaseFontSize,
        onDecreaseFontSize,
        onResetFontSize
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);

  if (!context) {
    throw new Error('useAccessibility mora biti unutar AccessibilityProvider!');
  }

  return context;
};
